import {
  faArrowRotateLeft,
  faBell,
  faBuilding,
  faMapLocationDot,
  faRightFromBracket,
  faUser,
  faUserXmark,
} from '@fortawesome/pro-light-svg-icons';
import {
  UserMenu,
  UserMenuAiCredits,
  UserMenuCompanyCard,
  UserMenuCompanySwitcher,
  UserMenuItem,
  UserMenuLink,
  UserMenuSection,
  UserMenuSeparator,
  UserMenuUserCard,
} from '@/components/shared/UserMenu';
import { useUserMenuIdentity } from '@/hooks/useUserMenuIdentity';
import { useClientUserMenu } from './hooks/useClientUserMenu';

/**
 * The user menu customers see. Every block is placed here, separators included,
 * so what the menu contains is readable in one screen.
 */
export function ClientUserMenu() {
  const identity = useUserMenuIdentity();
  const menu = useClientUserMenu();

  return (
    <UserMenu trigger={identity.trigger}>
      {identity.hasCompany && (
        <>
          <UserMenuCompanyCard company={identity.company}>
            {menu.companyId && (
              <UserMenuLink
                label={menu.getTranslation('viewProfile')}
                onClick={() => menu.onViewCompanyProfile(menu.companyId!)}
              />
            )}
          </UserMenuCompanyCard>
          <UserMenuSeparator />
        </>
      )}

      {menu.hasCompanies && (
        <>
          <UserMenuCompanySwitcher
            companies={menu.companies}
            search={menu.search}
            onSearchChange={menu.setSearch}
            onSelect={menu.handleSwitchCompany}
          />
          <UserMenuSeparator />
        </>
      )}

      {menu.showCredits && (
        <>
          <UserMenuAiCredits credits={menu.credits} />
          <UserMenuSeparator />
        </>
      )}

      <UserMenuUserCard user={identity.person} email={identity.email} />
      <UserMenuSeparator />

      <UserMenuSection>
        <UserMenuItem
          icon={faUser}
          label={menu.getTranslation('userProfile')}
          onClick={menu.onProfile}
        />
        {/* Notifications live in the header from `sm` up, so this entry is the
            narrow-screen way to reach them. */}
        <UserMenuItem
          icon={faBell}
          label={menu.getTranslation('notifications')}
          onClick={menu.onNotifications}
          className='sm:hidden'
          trailing={
            <span className='ml-auto w-1.5 h-1.5 rounded-full bg-destructive' />
          }
        />
        {/* Company Settings needs a company to administer — gated the same
            way "View profile" on the card above it is. */}
        {menu.companyId && (
          <UserMenuItem
            icon={faBuilding}
            label={menu.getTranslation('companySettings')}
            onClick={menu.onCompanySettings}
          />
        )}
        {menu.isImpersonating && (
          <UserMenuItem
            icon={faUserXmark}
            label={menu.getTranslation('endImpersonation')}
            onClick={menu.onEndImpersonation}
          />
        )}
        {/* Replay the tours. Always offered, unlike the banner entry below: a
            user who skipped a tour has nothing on screen telling them it
            existed, so there would be no way back to it. Clearing the state is
            enough — the tour arms itself again on whatever page has its
            anchors, and only one runs per page visit. */}
        <UserMenuItem
          icon={faMapLocationDot}
          label={menu.getTranslation('restartTour')}
          onClick={menu.onRestartTour}
        />
        {/* Only once the banner is dismissed — otherwise it is already on
            screen and this would be a second copy of the same action. */}
        {menu.hasOldFrontend && menu.bannerDismissed && (
          <UserMenuItem
            icon={faArrowRotateLeft}
            label={menu.getTranslation('showOldDashboardBanner')}
            onClick={menu.onShowOldDashboardBanner}
          />
        )}
      </UserMenuSection>

      <UserMenuSeparator />

      <UserMenuSection>
        <UserMenuItem
          icon={faRightFromBracket}
          label={menu.getTranslation('logOut')}
          onClick={menu.onLogout}
          destructive
        />
      </UserMenuSection>
    </UserMenu>
  );
}
